import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, ArrowLeft, ChevronRight, Lightbulb, Clock,
  Rocket, UserPlus, CalendarPlus, FileText, Receipt,
  Download, Cloud, BookOpen, Shield, Settings, Lock,
  MessageSquare, Smartphone, Plug, CreditCard, UserCheck,
  UsersRound, BarChart3, AlertTriangle, Layers,
} from 'lucide-react';
import {
  helpArticles,
  searchArticles,
  getArticleBySlug,
  getArticlesByCategory,
  CATEGORY_LABELS,
  type HelpCategory,
  type HelpArticle,
} from '../data/helpArticles';
import HelpArticleCard from '../components/HelpCenter/HelpArticleCard';
import './HelpCenter.css';

const ICON_MAP: Record<string, React.FC<{ size?: number }>> = {
  Rocket, UserPlus, CalendarPlus, Clock: Clock, FileText, Receipt,
  Download, Cloud, BookOpen, Shield, Settings, Lock,
  MessageSquare, Smartphone, Plug, CreditCard, UserCheck,
  UsersRound, BarChart3, AlertTriangle, Layers,
};

const ALL_CATEGORIES: HelpCategory[] = [
  'erste-schritte', 'klienten', 'termine', 'finanzen', 'methoden', 'einstellungen',
  'integrationen', 'nachrichten',
];

// --- Article Detail View ---

function ArticleDetail({ article }: { article: HelpArticle }) {
  const navigate = useNavigate();
  const IconComp = ICON_MAP[article.icon] || BookOpen;

  const relatedArticles = (article.relatedArticles || [])
    .map(getArticleBySlug)
    .filter((a): a is HelpArticle => !!a);

  return (
    <div className="help-detail">
      <button className="help-back-btn" onClick={() => navigate('/hilfe')}>
        <ArrowLeft size={16} />
        Alle Artikel
      </button>

      <div className="help-detail-header">
        <div className="help-detail-icon">
          <IconComp size={28} />
        </div>
        <div>
          <span className="help-detail-category">{CATEGORY_LABELS[article.category]}</span>
          <h1 className="help-detail-title">{article.title}</h1>
          {article.estimatedMinutes && (
            <span className="help-detail-time">
              <Clock size={14} />
              Lesezeit ca. {article.estimatedMinutes} Minuten
            </span>
          )}
        </div>
      </div>

      <div className="help-detail-body">
        {article.sections.map((section, i) => (
          <div key={i} className="help-section">
            {section.heading && <h2 className="help-section-heading">{section.heading}</h2>}
            {section.paragraphs.map((p, j) => (
              <p key={j} className="help-section-text">{p}</p>
            ))}
            {section.steps && (
              <ol className="help-steps">
                {section.steps.map((step, k) => (
                  <li key={k}>{step}</li>
                ))}
              </ol>
            )}
            {section.tip && (
              <div className="help-tip">
                <Lightbulb size={16} />
                <span>{section.tip}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {relatedArticles.length > 0 && (
        <div className="help-related">
          <h3>Verwandte Artikel</h3>
          <div className="help-related-list">
            {relatedArticles.map((ra) => (
              <button
                key={ra.id}
                className="help-related-item"
                onClick={() => navigate(`/hilfe/${ra.id}`)}
              >
                <span>{ra.shortTitle}</span>
                <ChevronRight size={14} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Help Center Main ---

export default function HelpCenter() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<HelpCategory | null>(null);

  // If slug provided, show article detail
  const detailArticle = slug ? getArticleBySlug(slug) : undefined;

  const filteredArticles = useMemo(() => {
    let results: HelpArticle[];
    if (query.trim()) {
      results = searchArticles(query);
    } else if (activeCategory) {
      results = getArticlesByCategory(activeCategory);
    } else {
      results = helpArticles;
    }
    return results;
  }, [query, activeCategory]);

  if (detailArticle) {
    return <ArticleDetail article={detailArticle} />;
  }

  const handleArticleClick = (article: HelpArticle) => {
    navigate(`/hilfe/${article.id}`);
  };

  return (
    <div className="help-center">
      <div className="help-center-header">
        <h1>Hilfe & Support</h1>
        <p className="text-muted">
          Finden Sie Antworten auf häufige Fragen und lernen Sie CoachDesk365 kennen.
        </p>
      </div>

      {/* Handbuch Banner */}
      <a
        href="/handbuch/"
        target="_blank"
        rel="noopener noreferrer"
        className="help-handbuch-banner"
      >
        <BookOpen size={24} />
        <div>
          <strong>Komplette Arbeitsanweisung</strong>
          <span>Alle Features, Funktionen und Optionen — Schritt für Schritt erklärt mit Screenshots.</span>
        </div>
        <ChevronRight size={20} />
      </a>

      {/* Search */}
      <div className="help-search-bar">
        <Search size={18} className="help-search-icon" />
        <input
          type="text"
          className="help-search-input"
          placeholder="Artikel durchsuchen..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveCategory(null); }}
        />
      </div>

      {/* Category Chips */}
      <div className="help-chips">
        <button
          className={`help-chip ${!activeCategory && !query ? 'help-chip-active' : ''}`}
          onClick={() => { setActiveCategory(null); setQuery(''); }}
        >
          Alle
        </button>
        {ALL_CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`help-chip ${activeCategory === cat ? 'help-chip-active' : ''}`}
            onClick={() => { setActiveCategory(cat); setQuery(''); }}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Article Grid */}
      {filteredArticles.length === 0 ? (
        <div className="help-empty">
          <Search size={32} />
          <p>Keine Artikel gefunden für "{query}"</p>
        </div>
      ) : (
        <div className="help-grid">
          {filteredArticles.map((article) => (
            <HelpArticleCard
              key={article.id}
              article={article}
              onClick={handleArticleClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
